import React, { useEffect, useState } from 'react';
import './VisualSalesScoreboard.css';
import NoTargetsPlaceholder from './NoTargetsPlaceholder';
import DatePicker from './DatePicker';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getDocFee, docFeeIncome } from '../utils/docFee';
import { overUnderFor } from '../utils/overUnder';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Color palette for individual performers
const PERFORMER_COLORS = [
  '#ff7a00', // Orange
  '#00d4ff', // Electric Blue
  '#10b981', // Green
  '#f59e0b', // Amber
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#f97316', // Deep Orange
];

function abbreviateNumber(value) {
  if (value === undefined || value === null || isNaN(value)) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'K';
  return value.toLocaleString();
}

function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '$0';
  return '$' + abbreviateNumber(Math.round(value));
}

function calculatePercent(actual, target) {
  if (!target || target === 0) return 0;
  return Math.round((actual / target) * 100);
}

function ScoreboardDropdown({ options, value, onChange }) {
  return (
    <select className="visual-scoreboard-dropdown" value={value} onChange={e => onChange(e.target.value)}>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

function getFiscalMetaForDealership(dealership) {
  const mode = dealership?.performanceYearMode || 'CALENDAR';
  const startMonth = Math.min(Math.max(Number(dealership?.fiscalYearStartMonth) || 1, 1), 12);
  const startDay = Math.min(Math.max(Number(dealership?.fiscalYearStartDay) || 1, 1), 28);
  const today = new Date();
  let fiscalStart = new Date(today.getFullYear(), startMonth - 1, startDay, 0, 0, 0, 0);
  if (mode === 'FISCAL' && today < fiscalStart) {
    fiscalStart.setFullYear(fiscalStart.getFullYear() - 1);
  }
  const fiscalEnd = new Date(fiscalStart);
  fiscalEnd.setFullYear(fiscalStart.getFullYear() + 1);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysInYear = Math.round((fiscalEnd.getTime() - fiscalStart.getTime()) / msPerDay);
  const daysElapsed = Math.max(0, Math.floor((today.getTime() - fiscalStart.getTime()) / msPerDay));
  const weeksInYear = Math.ceil(daysInYear / 7);
  const monthsInYear = 12;
  return { fiscalStart, fiscalEnd, daysInYear, daysElapsed, weeksInYear, monthsInYear };
}

function getMonthlyTarget(targets, field, currentMonth) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }
  const monthData = targets.monthlyTargets.find(m => m.month === currentMonth + 1);
  if (!monthData) return 0;
  return Number(monthData[field]) || 0;
}

// MTD target = current month's target prorated to days elapsed, so it lines up
// with the month-to-date actuals (which accumulate over the month) rather than
// summing the whole fiscal year.
function getMTDTarget(targets, field, referenceDate = new Date()) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }
  // Use the selected reference date's month/day so looking back at a past month
  // shows that month's target (prorated to the same day the MTD actuals stop at)
  // instead of the current calendar month.
  const ref = new Date(referenceDate);
  const refMonth = ref.getMonth();
  const monthlyTarget = getMonthlyTarget(targets, field, refMonth);
  const daysInMonth = new Date(ref.getFullYear(), refMonth + 1, 0).getDate();
  const daysElapsed = Math.min(ref.getDate(), daysInMonth);
  return Math.round(monthlyTarget * (daysElapsed / daysInMonth));
}

function convertTargetValue(targets, field, period, fiscalMeta) {
  if (!targets) return 0;
  const base = Number(targets[field]) || 0;
  const activityFields = ['greets', 'sitDowns', 'writeUps', 'testRides'];
  const storedFreq = activityFields.includes(field)
    ? (targets.metricsFrequency || 'daily')
    : (targets.frontEndFrequency || 'yearly');
  const fromFreq = (storedFreq || 'yearly').toLowerCase();
  const toFreq = (period || 'yearly').toLowerCase();
  let yearlyTotal;
  switch (fromFreq) {
    case 'daily':
      yearlyTotal = base * fiscalMeta.daysInYear;
      break;
    case 'weekly':
      yearlyTotal = base * fiscalMeta.weeksInYear;
      break;
    case 'monthly':
      yearlyTotal = base * fiscalMeta.monthsInYear;
      break;
    case 'yearly':
    default:
      yearlyTotal = base;
  }
  switch (toFreq) {
    case 'daily':
      return Math.round(yearlyTotal / fiscalMeta.daysInYear);
    case 'weekly':
      return Math.round(yearlyTotal / fiscalMeta.weeksInYear);
    case 'monthly':
      return Math.round(yearlyTotal / fiscalMeta.monthsInYear);
    case 'yearly':
    default:
      return Math.round(yearlyTotal);
  }
}

function computeMetrics(logs, activities, targets, dealership, referenceDate = new Date()) {
  const refDate = new Date(referenceDate);
  const now = refDate;
  const month = now.getMonth();
  const year = now.getFullYear();
  const fiscalMeta = getFiscalMetaForDealership(dealership);

  // Helper to parse UTC date and extract components without timezone conversion
  const getUTCDateComponents = (dateStr) => {
    const d = new Date(dateStr);
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth(),
      date: d.getUTCDate()
    };
  };

  // Get reference date components
  const refDateComp = {
    year: refDate.getFullYear(),
    month: refDate.getMonth(),
    date: refDate.getDate()
  };

  const dailyLogs = logs.filter((e) => {
    const logDateComp = getUTCDateComponents(e.date);
    return logDateComp.year === refDateComp.year &&
           logDateComp.month === refDateComp.month &&
           logDateComp.date === refDateComp.date;
  });

  // MTD: all data from start of month through the selected date (inclusive)
  const monthlyLogs = logs.filter((e) => {
    const logDateComp = getUTCDateComponents(e.date);
    return logDateComp.month === month &&
           logDateComp.year === year &&
           (logDateComp.year < year ||
            (logDateComp.year === year && logDateComp.month < month) ||
            (logDateComp.year === year && logDateComp.month === month && logDateComp.date <= refDateComp.date));
  });
  const sumLogs = (list, field) => list.reduce((sum, e) => sum + (Number(e[field]) || 0), 0);
  // The "monthly" activity cards compare MTD actuals against the target, so prorate
  // the full-month target to days elapsed (consistent with getMTDTarget for units).
  const daysInRefMonth = new Date(year, month + 1, 0).getDate();
  const mtdFactor = Math.min(refDateComp.date, daysInRefMonth) / daysInRefMonth;
  const mtdActivityTarget = (field) =>
    Math.round(convertTargetValue(targets, field, 'monthly', fiscalMeta) * mtdFactor);
  const greets = {
    daily: sumLogs(dailyLogs, 'greets'),
    monthly: sumLogs(monthlyLogs, 'greets'),
    dailyTarget: convertTargetValue(targets, 'greets', 'daily', fiscalMeta),
    monthlyTarget: mtdActivityTarget('greets')
  };
  const sitDowns = {
    daily: sumLogs(dailyLogs, 'sitDowns'),
    monthly: sumLogs(monthlyLogs, 'sitDowns'),
    dailyTarget: convertTargetValue(targets, 'sitDowns', 'daily', fiscalMeta),
    monthlyTarget: mtdActivityTarget('sitDowns')
  };
  const testRides = {
    daily: sumLogs(dailyLogs, 'testRides'),
    monthly: sumLogs(monthlyLogs, 'testRides'),
    dailyTarget: convertTargetValue(targets, 'testRides', 'daily', fiscalMeta),
    monthlyTarget: mtdActivityTarget('testRides')
  };
  const writeUps = {
    daily: sumLogs(dailyLogs, 'writeUps'),
    monthly: sumLogs(monthlyLogs, 'writeUps'),
    dailyTarget: convertTargetValue(targets, 'writeUps', 'daily', fiscalMeta),
    monthlyTarget: mtdActivityTarget('writeUps')
  };
  const dailyActivities = activities.filter((a) => {
    const saleDateComp = getUTCDateComponents(a.saleDate);
    return saleDateComp.year === refDateComp.year &&
           saleDateComp.month === refDateComp.month &&
           saleDateComp.date === refDateComp.date;
  });

  // MTD: all sales from start of month through the selected date (inclusive)
  const monthlyActivities = activities.filter((a) => {
    const saleDateComp = getUTCDateComponents(a.saleDate);
    return saleDateComp.month === month &&
           saleDateComp.year === year &&
           (saleDateComp.year < year ||
            (saleDateComp.year === year && saleDateComp.month < month) ||
            (saleDateComp.year === year && saleDateComp.month === month && saleDateComp.date <= refDateComp.date));
  });
  // Over/under on a trade is a cost of moving the unit, so it comes out of that
  // unit's gross at report time. The stored totalCost is left alone.
  const buildSalesBlock = (list, filterNew) => {
    const subset = list.filter((a) => {
      const val = (a.motorcycleNewUsed || '').toUpperCase();
      return filterNew ? val === 'NEW' : val === 'USED';
    });
    const count = subset.length;
    const gross = subset.reduce((sum, a) => {
      const price = Number(a.salesPrice) || 0;
      const cost = Number(a.totalCost) || 0;
      return sum + (price - cost - (overUnderFor(a) || 0));
    }, 0);
    return { count, gross };
  };
  const dailyNew = buildSalesBlock(dailyActivities, true);
  const dailyUsed = buildSalesBlock(dailyActivities, false);
  const monthlyNew = buildSalesBlock(monthlyActivities, true);
  const monthlyUsed = buildSalesBlock(monthlyActivities, false);
  const newUnitsMTD = getMTDTarget(targets, 'newUnits', refDate);
  const usedUnitsMTD = getMTDTarget(targets, 'usedUnits', refDate);
  const newGrossMTD = getMTDTarget(targets, 'newGrossProfit', refDate);
  const usedGrossMTD = getMTDTarget(targets, 'usedGrossProfit', refDate);
  const backEndGrossMTD = getMTDTarget(targets, 'backEndGrossProfit', refDate);
  const fiGrossMTD = getMTDTarget(targets, 'fiGrossProfit', refDate);
  const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const newUnitsDaily = Math.round(getMonthlyTarget(targets, 'newUnits', month) / daysInCurrentMonth);
  const usedUnitsDaily = Math.round(getMonthlyTarget(targets, 'usedUnits', month) / daysInCurrentMonth);
  const newGrossDaily = Math.round(getMonthlyTarget(targets, 'newGrossProfit', month) / daysInCurrentMonth);
  const usedGrossDaily = Math.round(getMonthlyTarget(targets, 'usedGrossProfit', month) / daysInCurrentMonth);
  const backEndGrossDaily = Math.round(getMonthlyTarget(targets, 'backEndGrossProfit', month) / daysInCurrentMonth);
  const fiGrossDaily = Math.round(getMonthlyTarget(targets, 'fiGrossProfit', month) / daysInCurrentMonth);
  const totalUnitsMTD = newUnitsMTD + usedUnitsMTD;
  const totalUnitsDaily = newUnitsDaily + usedUnitsDaily;
  const totalGrossMTD = newGrossMTD + usedGrossMTD + backEndGrossMTD;
  const totalGrossDaily = newGrossDaily + usedGrossDaily + backEndGrossDaily;
  const dailyTotalUnits = dailyNew.count + dailyUsed.count;
  const dailyTotalGross = dailyNew.gross + dailyUsed.gross;
  const monthlyTotalUnits = monthlyNew.count + monthlyUsed.count;
  const monthlyTotalGross = monthlyNew.gross + monthlyUsed.gross;
  // Doc fee is back-end gross too, so it joins the total while the reserve and
  // product breakdowns stay as F&I actually wrote them.
  const docFee = getDocFee(targets);
  const calculateFI = (activityList) => {
    const fiReserve = activityList.reduce((sum, a) => sum + (Number(a.fiReserve) || 0), 0);
    const fiProduct = activityList.reduce((sum, a) => sum + (Number(a.fiProduct) || 0), 0);
    const fiDocFee = docFeeIncome(activityList, docFee);
    return { fiReserve, fiProduct, fiDocFee, fiTotal: fiReserve + fiProduct + fiDocFee };
  };
  const dailyFI = calculateFI(dailyActivities);
  const monthlyFI = calculateFI(monthlyActivities);
  return {
    greets,
    sitDowns,
    testRides,
    writeUps,
    newUnits: {
      daily: dailyNew.count,
      monthly: monthlyNew.count,
      dailyTarget: newUnitsDaily,
      monthlyTarget: newUnitsMTD
    },
    newGross: {
      daily: dailyNew.gross,
      monthly: monthlyNew.gross,
      dailyTarget: newGrossDaily,
      monthlyTarget: newGrossMTD
    },
    usedUnits: {
      daily: dailyUsed.count,
      monthly: monthlyUsed.count,
      dailyTarget: usedUnitsDaily,
      monthlyTarget: usedUnitsMTD
    },
    usedGross: {
      daily: dailyUsed.gross,
      monthly: monthlyUsed.gross,
      dailyTarget: usedGrossDaily,
      monthlyTarget: usedGrossMTD
    },
    frontendTotal: {
      daily: dailyTotalGross,
      monthly: monthlyTotalGross,
      dailyTarget: totalGrossDaily,
      monthlyTarget: totalGrossMTD
    },
    frontendPerUnit: {
      daily: dailyTotalUnits > 0 ? dailyTotalGross / dailyTotalUnits : 0,
      monthly: monthlyTotalUnits > 0 ? monthlyTotalGross / monthlyTotalUnits : 0,
      dailyTarget: totalUnitsDaily > 0 ? totalGrossDaily / totalUnitsDaily : 0,
      monthlyTarget: totalUnitsMTD > 0 ? totalGrossMTD / totalUnitsMTD : 0
    },
    fiTotal: {
      daily: dailyFI.fiTotal,
      monthly: monthlyFI.fiTotal,
      dailyTarget: fiGrossDaily,
      monthlyTarget: fiGrossMTD
    },
    fiPerUnit: {
      daily: dailyTotalUnits > 0 ? dailyFI.fiTotal / dailyTotalUnits : 0,
      monthly: monthlyTotalUnits > 0 ? monthlyFI.fiTotal / monthlyTotalUnits : 0,
      dailyTarget: totalUnitsDaily > 0 ? fiGrossDaily / totalUnitsDaily : 0,
      monthlyTarget: totalUnitsMTD > 0 ? fiGrossMTD / totalUnitsMTD : 0
    },
    totalPerUnit: {
      daily: dailyTotalUnits > 0 ? (dailyTotalGross + dailyFI.fiTotal) / dailyTotalUnits : 0,
      monthly: monthlyTotalUnits > 0 ? (monthlyTotalGross + monthlyFI.fiTotal) / monthlyTotalUnits : 0,
      dailyTarget: totalUnitsDaily > 0 ? (totalGrossDaily + fiGrossDaily) / totalUnitsDaily : 0,
      monthlyTarget: totalUnitsMTD > 0 ? (totalGrossMTD + fiGrossMTD) / totalUnitsMTD : 0
    }
  };
}

export default function VisualSalesScoreboard({ user, managedOwner } = {}) {
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealership, setSelectedDealership] = useState('');
  const [selectedDealershipObj, setSelectedDealershipObj] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [metrics, setMetrics] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [hasTargets, setHasTargets] = useState(true);
  const [monthlyTrends, setMonthlyTrends] = useState(null);
  const [performerTrends, setPerformerTrends] = useState(null);
  const [cycleView, setCycleView] = useState(0); // 0: units, 1: gross profit, 2: activity

  useEffect(() => {
    async function fetchDealerships() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // In owner / managed-owner view, scope to that owner's dealerships so a
        // site manager viewing a specific owner doesn't see (or pick) every
        // dealership on the platform. Otherwise use the caller's own access.
        const isOwnerView = user?.role === 'OWNER' || managedOwner;
        let url = isOwnerView ? `${API_URL}/api/owners/dealerships` : `${API_URL}/api/dealerships`;
        if (managedOwner && isOwnerView) {
          url += `?ownerId=${managedOwner.id}`;
        }
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error('Failed to load dealerships');
        const json = await res.json();
        setDealerships(json.dealerships || []);
        if (json.dealerships && json.dealerships.length > 0) {
          setSelectedDealership(json.dealerships[0].id);
          setSelectedDealershipObj(json.dealerships[0]);
        }
      } catch (err) {
        setError(err.message || 'Failed to load dealerships');
      } finally {
        setLoading(false);
      }
    }
    fetchDealerships();
  }, [user, managedOwner]);

  useEffect(() => {
    if (!selectedDealership) return;
    async function fetchMetrics() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [logsRes, salesRes, targetsRes, leaderboardRes] = await Promise.all([
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/daily-logs`, { headers }),
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/sales-activities`, { headers }),
          fetch(`${API_URL}/api/targets/${selectedDealership}`, { headers }),
          fetch(`${API_URL}/api/analytics/leaderboard/sales?dealershipIds=${selectedDealership}&period=monthly`, { headers })
        ]);
        const logsData = logsRes.ok ? await logsRes.json() : { entries: [] };
        const salesData = salesRes.ok ? await salesRes.json() : { activities: [] };
        const targetsData = targetsRes.ok ? await targetsRes.json() : { frontEnd: {} };
        const leaderboardData = leaderboardRes.ok ? await leaderboardRes.json() : { leaderboard: [] };

        // When the dealership disables "always show F&I", unclosed deals don't
        // count toward any report figures until they're marked closed/committed.
        const visibleActivities = (selectedDealershipObj?.showUnclosedFI !== false)
          ? (salesData.activities || [])
          : (salesData.activities || []).filter((a) => a.closed);

        const computedMetrics = computeMetrics(
          logsData.entries || [],
          visibleActivities,
          targetsData.frontEnd || {},
          selectedDealershipObj,
          selectedDate
        );
        setMetrics(computedMetrics);
        setHasTargets(Array.isArray(targetsData.frontEnd?.monthlyTargets) && targetsData.frontEnd.monthlyTargets.length > 0);
        setLeaderboard(leaderboardData.leaderboard || []);

        // Compute monthly trends for charts
        const trends = computeMonthlyTrends(logsData.entries || [], visibleActivities);
        setMonthlyTrends(trends);

        // Compute per-performer trends
        const perfTrends = computePerformerTrends(logsData.entries || [], leaderboardData.leaderboard || []);
        setPerformerTrends(perfTrends);
      } catch (err) {
        setError(err.message || 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    }
    fetchMetrics();
  }, [selectedDealership, selectedDealershipObj, selectedDate]);

  // Auto-cycle through different views every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCycleView(prev => (prev + 1) % 3);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const computeMonthlyTrends = (logs, activities) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Get current month data (MTD)
    const months = [];
    const monthLabels = [];

    // Get days of current month for MTD trending
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const today = now.getDate();

    // Show last 7 days for MTD view
    for (let i = 6; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth, today - i);
      if (d.getDate() <= today && d.getMonth() === currentMonth) {
        months.push({ day: d.getDate(), month: d.getMonth(), year: d.getFullYear() });
        monthLabels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }
    }

    const greets = months.map(({ day, month, year }) => {
      return logs.filter(l => {
        const d = new Date(l.date);
        return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
      }).reduce((sum, l) => sum + (Number(l.greets) || 0), 0);
    });

    const sitDowns = months.map(({ day, month, year }) => {
      return logs.filter(l => {
        const d = new Date(l.date);
        return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
      }).reduce((sum, l) => sum + (Number(l.sitDowns) || 0), 0);
    });

    const testRides = months.map(({ day, month, year }) => {
      return logs.filter(l => {
        const d = new Date(l.date);
        return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
      }).reduce((sum, l) => sum + (Number(l.testRides) || 0), 0);
    });

    const writeUps = months.map(({ day, month, year }) => {
      return logs.filter(l => {
        const d = new Date(l.date);
        return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
      }).reduce((sum, l) => sum + (Number(l.writeUps) || 0), 0);
    });

    const sales = months.map(({ day, month, year }) => {
      return logs.filter(l => {
        const d = new Date(l.date);
        return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
      }).reduce((sum, l) => sum + (Number(l.sales) || 0), 0);
    });

    return { monthLabels, greets, sitDowns, testRides, writeUps, sales };
  };

  const computePerformerTrends = (logs, performers) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const today = now.getDate();

    // Get last 7 days labels
    const days = [];
    const dayLabels = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth, today - i);
      if (d.getDate() <= today && d.getMonth() === currentMonth) {
        days.push({ day: d.getDate(), month: d.getMonth(), year: d.getFullYear() });
        dayLabels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }
    }

    // Get top 5 performers
    const topPerformers = performers.slice(0, 5);

    const performerData = topPerformers.map(performer => {
      const dailySales = days.map(({ day, month, year }) => {
        return logs.filter(l => {
          const d = new Date(l.date);
          return d.getDate() === day &&
                 d.getMonth() === month &&
                 d.getFullYear() === year &&
                 l.userId === performer.employeeId;
        }).reduce((sum, l) => sum + (Number(l.sales) || 0), 0);
      });

      return {
        name: performer.name,
        employeeId: performer.employeeId,
        data: dailySales
      };
    });

    return { dayLabels, performers: performerData };
  };

  const handleDealershipChange = (dealershipId) => {
    setSelectedDealership(dealershipId);
    const dealershipObj = dealerships.find(d => d.id === dealershipId);
    setSelectedDealershipObj(dealershipObj || null);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement || !!document.mozFullScreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleFullscreen = () => {
    const scoreboardElement = document.querySelector('.visual-sales-scoreboard');
    if (!fullscreen && scoreboardElement) {
      if (scoreboardElement.requestFullscreen) {
        scoreboardElement.requestFullscreen();
      } else if (scoreboardElement.webkitRequestFullscreen) {
        scoreboardElement.webkitRequestFullscreen();
      } else if (scoreboardElement.mozRequestFullScreen) {
        scoreboardElement.mozRequestFullScreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
    }
  };

  if (loading) return <div className="scoreboard-loading">Loading...</div>;
  if (error) return <div className="scoreboard-error">{error}</div>;
  if (!dealerships.length) return <div className="scoreboard-error">No dealerships found.</div>;
  if (!metrics || !monthlyTrends || !performerTrends) return <div className="scoreboard-loading">Loading metrics...</div>;
  if (!hasTargets) return <NoTargetsPlaceholder />;

  const m = metrics;

  // Chart configuration
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          padding: 15,
          font: {
            size: 13,
            weight: 600
          },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        padding: 12,
        titleColor: '#ff7a00',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 122, 0, 0.3)',
        borderWidth: 1,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        }
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
          drawBorder: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: {
            size: 12
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: {
            size: 12
          }
        }
      }
    }
  };

  // Individual performer trend chart
  const performerChartData = {
    labels: performerTrends.dayLabels,
    datasets: performerTrends.performers.map((perf, idx) => ({
      label: perf.name,
      data: perf.data,
      borderColor: PERFORMER_COLORS[idx % PERFORMER_COLORS.length],
      backgroundColor: PERFORMER_COLORS[idx % PERFORMER_COLORS.length] + '20',
      fill: false,
      tension: 0.4,
      pointRadius: 5,
      pointBackgroundColor: PERFORMER_COLORS[idx % PERFORMER_COLORS.length],
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      borderWidth: 3
    }))
  };

  const activityChartData = {
    labels: monthlyTrends.monthLabels,
    datasets: [
      {
        label: 'Greets',
        data: monthlyTrends.greets,
        backgroundColor: 'rgba(255, 122, 0, 0.8)',
        borderRadius: 6
      },
      {
        label: 'Sit Downs',
        data: monthlyTrends.sitDowns,
        backgroundColor: 'rgba(0, 212, 255, 0.8)',
        borderRadius: 6
      },
      {
        label: 'Write Ups',
        data: monthlyTrends.writeUps,
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderRadius: 6
      }
    ]
  };

  // Dynamic pie chart data based on cycle
  let pieChartData, pieChartTitle;
  if (cycleView === 0) {
    pieChartData = {
      labels: ['New Units', 'Used Units'],
      datasets: [{
        data: [m.newUnits.monthly, m.usedUnits.monthly],
        backgroundColor: ['#ff7a00', '#00d4ff'],
        borderColor: ['#ff7a00', '#00d4ff'],
        borderWidth: 3
      }]
    };
    pieChartTitle = 'Units Split (MTD)';
  } else if (cycleView === 1) {
    pieChartData = {
      labels: ['Frontend Gross', 'F&I Gross'],
      datasets: [{
        data: [m.frontendTotal.monthly, m.fiTotal.monthly],
        backgroundColor: ['#ff7a00', '#00d4ff'],
        borderColor: ['#ff7a00', '#00d4ff'],
        borderWidth: 3
      }]
    };
    pieChartTitle = 'Gross Profit Split (MTD)';
  } else {
    pieChartData = {
      labels: ['New Gross', 'Used Gross'],
      datasets: [{
        data: [m.newGross.monthly, m.usedGross.monthly],
        backgroundColor: ['#ff7a00', '#00d4ff'],
        borderColor: ['#ff7a00', '#00d4ff'],
        borderWidth: 3
      }]
    };
    pieChartTitle = 'Frontend Split (MTD)';
  }

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          padding: 12,
          font: {
            size: 12,
            weight: 600
          },
          boxWidth: 12,
          boxHeight: 12
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        padding: 12,
        titleColor: '#ff7a00',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 122, 0, 0.3)',
        borderWidth: 1,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        color: '#fff',
        font: {
          size: 14,
          weight: 'bold'
        },
        formatter: (value, ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
          // Only show percentage if it's significant (>5%)
          return percentage > 5 ? `${percentage}%` : '';
        }
      }
    }
  };

  // Prepare stacked bar chart data for individual performers showing all metrics
  const performerStackedBarData = {
    labels: leaderboard.slice(0, 5).map(p => (p.name || 'Unknown').split(' ')[0]), // First name only to save space
    datasets: [
      {
        label: 'Sales',
        data: leaderboard.slice(0, 5).map(p => p.sales || p.bikesSold || 0),
        backgroundColor: '#ff7a00',
        borderRadius: 4,
        borderWidth: 0
      },
      {
        label: 'Write Ups',
        data: leaderboard.slice(0, 5).map(p => p.writeUps || 0),
        backgroundColor: '#10b981',
        borderRadius: 4,
        borderWidth: 0
      },
      {
        label: 'Test Rides',
        data: leaderboard.slice(0, 5).map(p => p.testRides || 0),
        backgroundColor: '#8b5cf6',
        borderRadius: 4,
        borderWidth: 0
      },
      {
        label: 'Sit Downs',
        data: leaderboard.slice(0, 5).map(p => p.sitDowns || 0),
        backgroundColor: '#00d4ff',
        borderRadius: 4,
        borderWidth: 0
      },
      {
        label: 'Greets',
        data: leaderboard.slice(0, 5).map(p => p.greets || 0),
        backgroundColor: '#f59e0b',
        borderRadius: 4,
        borderWidth: 0
      }
    ]
  };

  const stackedBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // Horizontal bars
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          padding: 12,
          font: {
            size: 12,
            weight: 600
          },
          usePointStyle: true,
          pointStyle: 'rect'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        padding: 12,
        titleColor: '#ff7a00',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 122, 0, 0.3)',
        borderWidth: 1,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        }
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
          drawBorder: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: {
            size: 11
          }
        }
      },
      y: {
        stacked: true,
        grid: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 13,
            weight: 600
          }
        }
      }
    }
  };

  return (
    <div className="visual-sales-scoreboard">
      <div className="visual-scoreboard-header">
        <div className="header-left">
          <h1>Sales Performance Dashboard</h1>
          <p className="header-subtitle">Month-to-Date Performance • Live Updates</p>
        </div>
        <div className="header-controls">
          <ScoreboardDropdown
            options={dealerships.map(d => ({ value: d.id, label: d.name }))}
            value={selectedDealership}
            onChange={handleDealershipChange}
          />
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            label=""
          />
          <button className="fullscreen-btn" onClick={handleFullscreen}>
            {fullscreen ? '✕ Exit' : '⛶ Fullscreen'}
          </button>
        </div>
      </div>

      <div className="visual-scoreboard-content">
        {/* Key Metrics Cards Row - Gauge Style */}
        <div className="metrics-row">
          <div className="metric-card">
            <div className="metric-label">Total Sales MTD</div>
            <div className="metric-gauge-container">
              <svg className="metric-gauge" viewBox="0 0 120 120">
                <circle className="gauge-bg" cx="60" cy="60" r="52" />
                <circle
                  className="gauge-fill"
                  cx="60"
                  cy="60"
                  r="52"
                  style={{
                    strokeDasharray: `${Math.min(calculatePercent(m.newUnits.monthly + m.usedUnits.monthly, m.newUnits.monthlyTarget + m.usedUnits.monthlyTarget), 100) * 3.27} 327`,
                    stroke: (m.newUnits.monthly + m.usedUnits.monthly) >= (m.newUnits.monthlyTarget + m.usedUnits.monthlyTarget) ? '#10b981' : '#ff7a00'
                  }}
                />
                <text className="gauge-value" x="60" y="60" textAnchor="middle" dominantBaseline="middle">
                  {m.newUnits.monthly + m.usedUnits.monthly}
                </text>
                <text className="gauge-percent" x="60" y="75" textAnchor="middle" dominantBaseline="middle">
                  {calculatePercent(m.newUnits.monthly + m.usedUnits.monthly, m.newUnits.monthlyTarget + m.usedUnits.monthlyTarget)}%
                </text>
              </svg>
            </div>
            <div className="metric-target">Target: {m.newUnits.monthlyTarget + m.usedUnits.monthlyTarget}</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Gross Profit MTD</div>
            <div className="metric-gauge-container">
              <svg className="metric-gauge" viewBox="0 0 120 120">
                <circle className="gauge-bg" cx="60" cy="60" r="52" />
                <circle
                  className="gauge-fill"
                  cx="60"
                  cy="60"
                  r="52"
                  style={{
                    strokeDasharray: `${Math.min(calculatePercent(m.frontendTotal.monthly + m.fiTotal.monthly, m.frontendTotal.monthlyTarget + m.fiTotal.monthlyTarget), 100) * 3.27} 327`,
                    stroke: (m.frontendTotal.monthly + m.fiTotal.monthly) >= (m.frontendTotal.monthlyTarget + m.fiTotal.monthlyTarget) ? '#10b981' : '#ff7a00'
                  }}
                />
                <text className="gauge-value" x="60" y="55" textAnchor="middle" dominantBaseline="middle">
                  {formatCurrency(m.frontendTotal.monthly + m.fiTotal.monthly)}
                </text>
                <text className="gauge-percent" x="60" y="75" textAnchor="middle" dominantBaseline="middle">
                  {calculatePercent(m.frontendTotal.monthly + m.fiTotal.monthly, m.frontendTotal.monthlyTarget + m.fiTotal.monthlyTarget)}%
                </text>
              </svg>
            </div>
            <div className="metric-target">Target: {formatCurrency(m.frontendTotal.monthlyTarget + m.fiTotal.monthlyTarget)}</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Profit per Unit MTD</div>
            <div className="metric-gauge-container">
              <svg className="metric-gauge" viewBox="0 0 120 120">
                <circle className="gauge-bg" cx="60" cy="60" r="52" />
                <circle
                  className="gauge-fill"
                  cx="60"
                  cy="60"
                  r="52"
                  style={{
                    strokeDasharray: `${Math.min(calculatePercent(m.totalPerUnit.monthly, m.totalPerUnit.monthlyTarget), 100) * 3.27} 327`,
                    stroke: m.totalPerUnit.monthly >= m.totalPerUnit.monthlyTarget ? '#10b981' : '#ff7a00'
                  }}
                />
                <text className="gauge-value" x="60" y="55" textAnchor="middle" dominantBaseline="middle">
                  {formatCurrency(m.totalPerUnit.monthly)}
                </text>
                <text className="gauge-percent" x="60" y="75" textAnchor="middle" dominantBaseline="middle">
                  {calculatePercent(m.totalPerUnit.monthly, m.totalPerUnit.monthlyTarget)}%
                </text>
              </svg>
            </div>
            <div className="metric-target">Target: {formatCurrency(m.totalPerUnit.monthlyTarget)}</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Conversion Rate MTD</div>
            <div className="metric-gauge-container">
              <svg className="metric-gauge" viewBox="0 0 120 120">
                <circle className="gauge-bg" cx="60" cy="60" r="52" />
                <circle
                  className="gauge-fill"
                  cx="60"
                  cy="60"
                  r="52"
                  style={{
                    strokeDasharray: `${Math.min(m.greets.monthly > 0 ? Math.round(((m.newUnits.monthly + m.usedUnits.monthly) / m.greets.monthly) * 100) : 0, 100) * 3.27} 327`,
                    stroke: '#00d4ff'
                  }}
                />
                <text className="gauge-value" x="60" y="55" textAnchor="middle" dominantBaseline="middle">
                  {m.greets.monthly > 0 ? Math.round(((m.newUnits.monthly + m.usedUnits.monthly) / m.greets.monthly) * 100) : 0}%
                </text>
                <text className="gauge-label" x="60" y="75" textAnchor="middle" dominantBaseline="middle">
                  Greets→Sales
                </text>
              </svg>
            </div>
            <div className="metric-target">
              {m.sitDowns.monthly > 0 ? Math.round(((m.newUnits.monthly + m.usedUnits.monthly) / m.sitDowns.monthly) * 100) : 0}% close rate
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="charts-grid">
          {/* Individual Performer Metrics - Stacked Bar Chart */}
          <div className="chart-card large">
            <div className="chart-header">
              <h3>Top Performer Activity Breakdown</h3>
              <span className="chart-subtitle">MTD Performance Metrics</span>
            </div>
            <div className="chart-container">
              {leaderboard.length > 0 ? (
                <Bar data={performerStackedBarData} options={stackedBarOptions} />
              ) : (
                <div className="no-data">No performance data available</div>
              )}
            </div>
          </div>

          {/* Top Performers Leaderboard */}
          <div className="chart-card leaderboard-card-large">
            <div className="chart-header">
              <h3>Leaderboard</h3>
              <span className="chart-subtitle">Month to Date</span>
            </div>
            <div className="leaderboard-list-large">
              {leaderboard.length === 0 ? (
                <div className="no-data">No performance data</div>
              ) : (
                leaderboard.slice(0, 5).map((performer, idx) => (
                  <div key={performer.employeeId || idx} className="leaderboard-item-large">
                    <div className="rank-badge-large" style={{ backgroundColor: PERFORMER_COLORS[idx % PERFORMER_COLORS.length] + '30', borderColor: PERFORMER_COLORS[idx % PERFORMER_COLORS.length] }}>
                      #{idx + 1}
                    </div>
                    <div className="performer-info-large">
                      <div className="performer-name-large">{performer.name || 'Unknown'}</div>
                      <div className="performer-stats-large">
                        <span className="stat-item">{performer.sales || performer.bikesSold || 0} units</span>
                        <span className="stat-item">{performer.greets || 0} greets</span>
                        <span className="stat-item">{performer.sitDowns || 0} sits</span>
                      </div>
                    </div>
                    <div className="performer-value-large">{formatCurrency(performer.totalGross || 0)}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Animated Pie Chart with Better Labels */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>{pieChartTitle}</h3>
              <span className="chart-subtitle cycle-indicator">◉ View {cycleView + 1} of 3</span>
            </div>
            <div className="chart-container">
              <Doughnut data={pieChartData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
